/* eslint-disable */
// @ts-nocheck
import * as R from 'ramda';
import type { Row } from '../../../../main/types/tableTypes';
import { roundToFixed2 } from '../../../../main/support/utils';
import appendNumericMetricWithEventData from './appendNumericMetricWithEventData';

/* --------------INTENDED ONLY AS HELPERS FOR OTHER UTILS--------------------
------------(you will not need to import these unless testing)--------------*/

/**
 * Helper function to calculate avg based on previous and current avg/total_count
 *
 * @param {number} pAvgHours   - previous iterations average hours
 * @param {number} pTotalCount - previous event total_count value
 * @param {number} cHours - current hours to last event
 * @param {number} cEventCount - current event total_count
 * @return {number} update averages hours
 */
export function calculateNewAvgHoursFromEvent(
  pAvgHours: number,
  pTotalCount: number,
  cHours: number,
  cEventCount: number
): number {
  if (R.isNil(cHours)) {
    return pAvgHours;
  }

  if (R.isNil(pAvgHours)) {
    return cHours / cEventCount;
  }

  const pTotalHours = pAvgHours * pTotalCount;
  const cTotalHours = cHours * cEventCount;
  const totalCount = pTotalCount + cEventCount;
  const updatedAvgHours = (pTotalHours + cTotalHours) / totalCount;
  return updatedAvgHours;
}

/**
 * Helper function to calculate ratio based on previous ratio, previous total count (denom), and the incoming count
 *
 * @param {number} pRatio   - previous iterations ratio
 * @param {number} pTotalCount - previous event total_count value
 * @param {number} cCount - current count for ratio. NOTE: this is not always equivalent to current event total_count
 * @param {number} cTotalCount - current event total_count + previous event total_count
 * @return {number} update ratio
 */
export function calculateNewRatio(pRatio: number = 0, pTotalCount: number = 0, cCount: number, cTotalCount: number): number {
  // for the null cases. undefined cases handled in default param values
  pRatio = !pRatio ? 0 : pRatio;
  pTotalCount = !pTotalCount ? 0 : pTotalCount;
  const pCount = pRatio * pTotalCount;
  return cTotalCount !== 0 ? (cCount + pCount) / cTotalCount : 0;
}
const TOUCH_TO_EVENT_BUCKET_MAX = {
  seconds_from_last_attributed_touch_to_event: 60,
  days_from_last_attributed_touch_to_event: 90,
};

/**
 * Helper function to generate name new touch to event dimension name e.g. eo_install_seconds_from_last_attributed_touch_to_event_under_10
 *
 * @param {number} dataSourceName      - name of data source
 * @param {Array<number>} timeInterval - time interval that return value represents
 * @param {string} touchToEventName    - name of attributed touch to event dimension e.g. seconds_from_last_attributed_touch_to_event
 * @return {string} generated dimension name used by transformer
 */
export function generateTouchToEventName(
  dataSourceName: string,
  timeInterval: Array<number>,
  touchToEventName: string
): string {
  const base = `${dataSourceName}_${touchToEventName}`;

  if (timeInterval[0] < 1) {
    return `${base}_under_${timeInterval[1] + 1}`;
  }

  if (timeInterval[1] >= TOUCH_TO_EVENT_BUCKET_MAX[touchToEventName]) {
    return `${base}_over_${timeInterval[0]}`;
  }

  return `${base}_between_${timeInterval[0]}_and_${timeInterval[1] + 1}`;
}

/**
 * Helper function that checks whether a number falls within a range of numbers
 *
 * @param {number} touchToEventValue   - number being checked
 * @param {Array<number>} timeInterval - time interval that return value represents
 * @return {boolean} whether touchToEventValue was contained within timeInterval including the start value and the end value
 */
export function checkTouchValueWithinInterval(touchToEventValue: number, timeInterval: Array<number>): boolean {
  return (touchToEventValue - timeInterval[0]) * (touchToEventValue - timeInterval[1]) <= 0;
}

/**
 * Helper function that returns an updated ratio given whether the touch to event dimension value in question falls within a certain time interval
 *
 * @param {number} previousTouchToEventRatio - previous ratio
 * @param {number} previousTotalCount        - previous event total_count value
 * @param {number} currentCount              - current event total_count value
 * @param {number} touchToEventValue         - current event touch to event value in question
 * @param {Array<number>} timeInterval       - a start and end value
 * @return {number} updated ratio
 */
export function generateCurrentRatio(
  previousTouchToEventRatio: number = 0,
  previousTotalCount: number = 0,
  currentCount: number = 0,
  touchToEventValue: number = 0,
  timeInterval: Array<number>
): number {
  const currentTotalCount = previousTotalCount + currentCount;
  const isTouchValueWithinInterval = checkTouchValueWithinInterval(touchToEventValue, timeInterval);
  currentCount = isTouchValueWithinInterval ? currentCount : 0;
  return calculateNewRatio(previousTouchToEventRatio, previousTotalCount, currentCount, currentTotalCount);
}

/**
 * Creates a transformer function for given data source
 *
 * @param {string} dataSourceName       - name of the data source to transform
 * @param {Object} previousValueStorage - object for storaging previous avg hours and event count
 * @return {function} transformer function for given data source
 */
export function getHoursFromEventTransformer(dataSourceName: string): Record<string, any> {
  return ({ event = {}, metric = 'total_count', row = {} }) => {
    const hoursFromLastColumnName = `${dataSourceName}_hours_from_last_attributed_touch_to_event`;
    const currentCount = +event[metric] || 0;
    const updatedCount = (row[dataSourceName] || 0) + currentCount;
    const updatedAvgHours = calculateNewAvgHoursFromEvent(
      row[hoursFromLastColumnName],
      row[dataSourceName],
      event.hours_from_last_attributed_touch_to_event,
      currentCount
    );
    return {
      // TODO remove after DATA-1440 complete
      [dataSourceName]: Math.round(parseFloat(updatedCount)),
      [`${dataSourceName}_hours_from_last_attributed_touch_to_event`]: updatedAvgHours,
    };
  };
}

/**
 * Creates a transformer function given a data source and attributed touch to install time intervals
 *
 * @param {string} dataSourceName - name of the data source to transform
 * @param {Object} buckets        - an Object with keys as touch_to_event dimensions and values as the desired time intervals for these dimensions
 * {
 *    seconds_from_last_attributed_touch_to_event: [[0, 9], [10, 29], [30, 60]],
 *    hours_from_last_attributed_touch_to_event: [[0, 1], [1, 90]] - assuming 90 is the upper bound for days
 * }
 * @return {function} transformer function for given data source
 */
export function getTouchToEventBucketTransformer(dataSourceName: string, buckets: Record<string, any>): Record<string, any> {
  return ({ event = {}, metric = 'total_count', row = {} }) => {
    let currentCount = !event[metric] || event[metric] < 0 ? 0 : +event[metric];
    const previousTotalCount = row[dataSourceName] || 0;
    const currentTotalCount = previousTotalCount + currentCount;
    return Object.keys(buckets).reduce(
      (dimensions, bucketKey) => {
        return R.merge(
          dimensions,
          buckets[bucketKey].reduce((acc, timeInterval) => {
            const touchToEventName = generateTouchToEventName(dataSourceName, timeInterval, bucketKey);
            // we do not want to count a value if the bucket is null i.e. if seconds_from_last_attributed_touch_to_event is null
            currentCount = R.isNil(event[bucketKey]) ? 0 : currentCount;
            const currentRatio = generateCurrentRatio(
              row[touchToEventName],
              previousTotalCount,
              currentCount,
              +event[bucketKey] || 0,
              timeInterval
            );
            return R.merge(acc, {
              [touchToEventName]: currentRatio,
            });
          }, {})
        );
      },
      {
        [dataSourceName]: currentTotalCount,
      }
    );
  };
}

/* ---------------------Reusable Druid TRANSFORMERS----------------------------
---------(these are the transformers you are intended to import)-------------*/

/*
 * These transformers are functions that are passed the event from populateRowsFromGroupBy
 * Some wrap the transformer in another function, so you can pass them other
 * arguments as well. They should return an object with the key of the data source
 * specified, and the value is the transformer function itself.
 *
 * Note that each transformer function is passed an object as an argument,
 * containing the keys: { event, metric, dimensions, and row }.
 *
 */

/**
 * Generates transformers for calculating the avg hours from last event
 * Values need to be recalculated everytime another event with same dimensions is transformed
 * Currently used for eo_install and eo_reinstall
 *
 * @param {Array} dataSourceNames - list events to transform ['eo_install', 'eo_reinstall']
 * @return {Object} object containing transformer functions
 * {
 *   eo_install: (event, dimensions) => {
 *     ...
 *   }
 * }
 */
export function generateHoursFromEventTransformers(dataSourceNames: string[]): Record<string, any> {
  return dataSourceNames.reduce((transformers, dataSourceName) => {
    transformers[dataSourceName] = getHoursFromEventTransformer(dataSourceName);
    return transformers;
  }, {});
}
export function generateTouchToEventTransformers(
  dataSourceNames: string[],
  touchToEventBucketsByDataSource: Record<string, any>
): Record<string, any> {
  return dataSourceNames.reduce((transformers, dataSourceName) => {
    transformers[dataSourceName] = getTouchToEventBucketTransformer(
      dataSourceName,
      touchToEventBucketsByDataSource[dataSourceName]
    );
    return transformers;
  }, {});
}
type TransformerPayload = {
  event: Record<string, any>;
  metric: string;
  row: Row;
};

/**
 * Transformers for druid standard datasources. These are global and should only
 * be amended/appended to if we're confident that we want the transformer globally.
 * Otherwise pass transform in as a query param (see @hydrateRowsFromDataSources)
 *
 * IMPORTANT: must check row for existing value or else it may get overwritten.
 * this happens when there is a dimension that is added to query but not added to
 * the table. (e.g. hours_from_last_attributed_touch_to_event)
 */
export const STANDARD_DATASOURCE_TRANSFORMERS = {
  eo_commerce_event: function ({ event = {}, metric = 'total_count', row = {} }: TransformerPayload): Record<string, any> {
    if (!event.name) {
      return {};
    }

    const columnKey = event.name.toUpperCase();

    const countParsingFunction = (count) => Math.round(parseFloat(count));

    const count = appendNumericMetricWithEventData(row[columnKey], event[metric], countParsingFunction);
    let revenueEvent = {};

    // Only use PURCHASE revenue for revenue values
    if (columnKey === 'PURCHASE') {
      const revenueParsingFunction = (revenue) => roundToFixed2(revenue);

      const revenue = appendNumericMetricWithEventData(row.revenue, event.revenue, revenueParsingFunction);
      const revenueInLocalCurrency = appendNumericMetricWithEventData(
        row.revenue_in_local_currency,
        event.revenue_in_local_currency,
        revenueParsingFunction
      );
      revenueEvent = {
        revenue,
        revenue_in_local_currency: revenueInLocalCurrency,
      };
    }

    return {
      // TODO remove after DATA-1440 complete
      [columnKey]: count,
      ...revenueEvent,
    };
  },
  eo_custom_event: function ({ event = {}, metric = 'total_count', row = {} }: TransformerPayload): Record<string, any> {
    if (!event.name) {
      return {};
    }

    const columnKey = `eo_custom_event-${event.name}`;
    const count = appendNumericMetricWithEventData(row[columnKey], event[metric]);
    return {
      [columnKey]: count,
    };
  },
  eo_content_event: function ({ event = {}, metric = 'total_count', row = {} }: TransformerPayload): Record<string, any> {
    if (!event.name) {
      return {};
    }

    const count = appendNumericMetricWithEventData(row[event.name], event[metric]);
    return {
      [event.name]: count,
    };
  },
  eo_user_lifecycle_event: function ({
    event = {},
    metric = 'total_count',
    row = {},
  }: TransformerPayload): Record<string, any> {
    if (!event.name) {
      return {};
    }

    const count = appendNumericMetricWithEventData(row[event.name], event[metric]);
    return {
      [event.name]: count,
    };
  },
};
export const STANDARD_BLOCKED_DATASOURCE_TRANSFORMERS = {
  eo_commerce_event_blocked: function ({
    event = {},
    metric = 'total_count',
    row = {},
  }: TransformerPayload): Record<string, any> {
    if (!event.name) {
      return {};
    }

    const columnKey = event.name.toUpperCase() + '_BLOCKED';

    const countParsingFunction = (count) => Math.round(parseFloat(count));

    const count = appendNumericMetricWithEventData(row[columnKey], event[metric], countParsingFunction);
    let revenueEvent = {};

    // Only use PURCHASE revenue for revenue values
    if (columnKey === 'PURCHASE_BLOCKED') {
      const revenueParsingFunction = (revenue) => roundToFixed2(revenue);

      const revenue = appendNumericMetricWithEventData(row.revenue, event.revenue, revenueParsingFunction);
      revenueEvent = {
        revenue_blocked: revenue,
      };
    }

    return {
      // TODO remove after DATA-1440 complete
      [columnKey]: count,
      ...revenueEvent,
    };
  },
  eo_custom_event_blocked: function ({
    event = {},
    metric = 'total_count',
    row = {},
  }: TransformerPayload): Record<string, any> {
    if (!event.name) {
      return {};
    }

    const columnKey = `eo_custom_event_blocked-${event.name}`;
    const count = appendNumericMetricWithEventData(row[columnKey], event[metric]);
    return {
      [columnKey]: count,
    };
  },
  eo_content_event_blocked: function ({
    event = {},
    metric = 'total_count',
    row = {},
  }: TransformerPayload): Record<string, any> {
    if (!event.name) {
      return {};
    }

    const columnKey = event.name.toUpperCase() + '_BLOCKED';
    const count = appendNumericMetricWithEventData(row[columnKey], event[metric]);
    return {
      [columnKey]: count,
    };
  },
  eo_user_lifecycle_event_blocked: function ({
    event = {},
    metric = 'total_count',
    row = {},
  }: TransformerPayload): Record<string, any> {
    if (!event.name) {
      return {};
    }

    const columnKey = event.name.toUpperCase() + '_BLOCKED';
    const count = appendNumericMetricWithEventData(row[columnKey], event[metric]);
    return {
      [columnKey]: count,
    };
  },
};
export const COST_TRANSFORMER = {
  cost: function ({ event = {}, row = {} }: TransformerPayload): Record<string, any> {
    const cost = appendNumericMetricWithEventData(row.cost, event.cost);
    return {
      cost,
    };
  },
  cost_in_app_local_currency: function ({ event = {}, row = {} }: TransformerPayload): Record<string, any> {
    const cost_in_app_local_currency = appendNumericMetricWithEventData(
      row.cost_in_app_local_currency,
      event.cost_in_app_local_currency
    );
    return {
      cost_in_app_local_currency,
    };
  },
  eCPI: function ({ event = {}, row = {} }: TransformerPayload): Record<string, any> {
    const calculated_eCPI = appendNumericMetricWithEventData(null, event.cost);
    return {
      calculated_eCPI,
    };
  },
  eCPC: function ({ event = {}, row = {} }: TransformerPayload): Record<string, any> {
    const calculated_eCPC = appendNumericMetricWithEventData(null, event.cost);
    return {
      calculated_eCPC,
    };
  },
  eCPM: function ({ event = {}, row = {} }: TransformerPayload): Record<string, any> {
    const calculated_eCPM = appendNumericMetricWithEventData(null, event.cost);
    return {
      calculated_eCPM,
    };
  },
  eCPA: function ({ event = {}, row = {} }: TransformerPayload): Record<string, any> {
    const calculated_eCPA = appendNumericMetricWithEventData(null, event.cost);
    return {
      calculated_eCPA,
    };
  },
  eCPI_in_local_currency: function ({ event = {}, row = {} }: TransformerPayload): Record<string, any> {
    const calculated_eCPI_in_local_currency = appendNumericMetricWithEventData(null, event.cost_in_app_local_currency);
    return {
      calculated_eCPI_in_local_currency,
    };
  },
  eCPC_in_local_currency: function ({ event = {}, row = {} }: TransformerPayload): Record<string, any> {
    const calculated_eCPC_in_local_currency = appendNumericMetricWithEventData(null, event.cost_in_app_local_currency);
    return {
      calculated_eCPC_in_local_currency,
    };
  },
  eCPM_in_local_currency: function ({ event = {}, row = {} }: TransformerPayload): Record<string, any> {
    const calculated_eCPM_in_local_currency = appendNumericMetricWithEventData(null, event.cost_in_app_local_currency);
    return {
      calculated_eCPM_in_local_currency,
    };
  },
  eCPA_in_local_currency: function ({ event = {}, row = {} }: TransformerPayload): Record<string, any> {
    const calculated_eCPA_in_local_currency = appendNumericMetricWithEventData(null, event.cost_in_app_local_currency);
    return {
      calculated_eCPA_in_local_currency,
    };
  },
  ROAS: function ({ event = {}, row = {} }: TransformerPayload): Record<string, any> {
    const calculated_ROAS = appendNumericMetricWithEventData(null, event.percentage);
    return {
      calculated_ROAS,
    };
  },
  ROI: function ({ event = {}, row = {} }: TransformerPayload): Record<string, any> {
    const calculated_ROI = appendNumericMetricWithEventData(null, event.percentage);
    return {
      calculated_ROI,
    };
  },
};
